export const reasons = [
  ['DEDICATED', 'Dedicado'],
  ['MAINTENANCE', 'Manutenção'],
  ['TIME_OFF', 'Folga'],
  ['BLOCKED', 'Bloqueio'],
];
export const blocks = [
  ['CARGO_THEFT', 'Roubo de cargas'],
  ['DRIVER_MISCONDUCT', 'Má conduta do motorista'],
];
export const initialForm = () => ({
  corridor: false,
  available: true,
  responsible: null,
  contactPhone: '',
  costCenter: null,
  unavailabilityReason: null,
  dedicatedClient: null,
  unavailableFrom: '',
  unavailableUntil: '',
  blockReason: null,
});

const formatter = new Intl.DateTimeFormat('sv-SE', {
  timeZone: 'America/Sao_Paulo',
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
  second: '2-digit',
  hourCycle: 'h23',
});
export function localTime(iso) {
  if (!iso) return '';
  const date = new Date(iso);
  if (!Number.isFinite(date.getTime())) throw new Error('Período recebido inválido.');
  const parts = Object.fromEntries(
    formatter.formatToParts(date).map(({ type, value }) => [type, value]),
  );
  return `${parts.year}-${parts.month}-${parts.day}T${parts.hour}:${parts.minute}:${parts.second}.${String(date.getUTCMilliseconds()).padStart(3, '0')}`;
}
export function utcTime(value) {
  if (!/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(?::\d{2}(?:\.\d{1,3})?)?$/.test(value || '')) return null;
  const naive = Date.parse(`${value}Z`);
  if (!Number.isFinite(naive) || Number(value.slice(0, 4)) < 1900) return null;
  if (new Date(naive).toISOString().slice(0, 16) !== value.slice(0, 16)) return null;
  // Obtém offsets da zona, inclusive nas transições históricas; rejeita horário inexistente/ambíguo.
  const candidates = new Set();
  for (const shift of [-86400000, 0, 86400000]) {
    const probe = naive + shift;
    const offset = Date.parse(`${localTime(new Date(probe).toISOString())}Z`) - probe;
    const candidate = new Date(naive - offset).toISOString();
    if (Date.parse(`${localTime(candidate)}Z`) === naive) candidates.add(candidate);
  }
  return candidates.size === 1 ? [...candidates][0] : null;
}
export function fromResponse(data) {
  if (
    !data ||
    typeof data.available !== 'boolean' ||
    typeof data.corridor !== 'boolean' ||
    !Number.isInteger(data.version)
  )
    throw new Error('Não foi possível interpretar os dados de disponibilidade da placa.');
  return {
    ...initialForm(),
    corridor: data.corridor,
    available: data.available,
    responsible:
      data.responsible ||
      (data.responsibleUserId
        ? { id: data.responsibleUserId, name: `Usuário ${data.responsibleUserId}` }
        : null),
    costCenter:
      data.costCenter ||
      (data.costCenterId
        ? { id: data.costCenterId, name: `Centro de custo ${data.costCenterId}` }
        : null),
    dedicatedClient:
      data.dedicatedClient ||
      (data.dedicatedClientId
        ? { id: data.dedicatedClientId, name: `Cliente ${data.dedicatedClientId}` }
        : null),
    contactPhone: data.contactPhone || '',
    unavailabilityReason: data.unavailabilityReason,
    unavailableFrom: localTime(data.unavailableFrom),
    unavailableUntil: localTime(data.unavailableUntil),
    blockReason: data.blockReason,
  };
}
export function changeForm(form, field, value) {
  if (field === 'available' || field === 'unavailabilityReason')
    return {
      ...form,
      dedicatedClient: null,
      unavailableFrom: '',
      unavailableUntil: '',
      blockReason: null,
      unavailabilityReason: field === 'available' ? null : value,
      [field]: value,
    };
  return { ...form, [field]: value };
}
export function toPayload(form, version = 0) {
  const temporary =
    !form.available && ['MAINTENANCE', 'TIME_OFF'].includes(form.unavailabilityReason);
  return {
    expectedVersion: version,
    corridor: form.corridor,
    available: form.available,
    responsibleUserId: form.responsible?.id || null,
    contactPhone: form.contactPhone.trim() || null,
    costCenterId: form.costCenter?.id || null,
    unavailabilityReason: form.available ? null : form.unavailabilityReason,
    dedicatedClientId:
      !form.available && form.unavailabilityReason === 'DEDICATED'
        ? form.dedicatedClient?.id || null
        : null,
    unavailableFrom: temporary ? utcTime(form.unavailableFrom) : null,
    unavailableUntil: temporary ? utcTime(form.unavailableUntil) : null,
    blockReason:
      !form.available && form.unavailabilityReason === 'BLOCKED' ? form.blockReason : null,
  };
}
export function validateForm(form) {
  const errors = {};
  const phone = form.contactPhone.trim();
  if (phone && (phone.length > 30 || !/^[+\d\s().-]+$/.test(phone) || !/\d/.test(phone)))
    errors.contactPhone = 'Informe um telefone válido com até 30 caracteres.';
  if (!form.available) {
    if (!reasons.some(([value]) => value === form.unavailabilityReason))
      errors.unavailabilityReason = 'Selecione o motivo.';
    if (form.unavailabilityReason === 'DEDICATED' && !form.dedicatedClient?.id)
      errors.dedicatedClientId = 'Selecione um cliente com CNPJ.';
    if (
      form.unavailabilityReason === 'BLOCKED' &&
      !blocks.some(([value]) => value === form.blockReason)
    )
      errors.blockReason = 'Selecione o motivo do bloqueio.';
    if (['MAINTENANCE', 'TIME_OFF'].includes(form.unavailabilityReason)) {
      const start = utcTime(form.unavailableFrom);
      const end = utcTime(form.unavailableUntil);
      if (!start) errors.unavailableFrom = 'Informe data e hora válidas em São Paulo.';
      if (!end) errors.unavailableUntil = 'Informe data e hora válidas em São Paulo.';
      else if (start && end <= start)
        errors.unavailableUntil = 'O término deve ser posterior ao início.';
    }
  }
  return errors;
}
export const sameSettings = (a, b) => JSON.stringify(toPayload(a)) === JSON.stringify(toPayload(b));
